
// src/modules/clients/pages/EditClientPage.tsx
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Alert,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  EmojiEvents as PointsIcon,
  Save as SaveIcon,
  Launch as SaveViewIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

type Form = {
  fullName: string;
  email: string;
  phone: string;
  address: string;
  loyaltyPoints: number;
};

type Errors = Partial<Record<keyof Form, string>>;

export default function EditClientPage() {
  useProtectedRoute();
  const { token } = useAuth();
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState<Form>({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    loyaltyPoints: 0,
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<false | "save" | "saveView">(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errors>({});

  // --------- Fetch current client ----------
  useEffect(() => {
    let cancelled = false;
    const fetchClient = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/clients/${clientId}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const c = res.data?.client ?? {};
        setForm({
          fullName: c.fullName ?? "",
          email: c.email ?? "",
          phone: c.phone ?? "",
          address: c.address ?? "",
          loyaltyPoints: Number(c.loyaltyPoints ?? 0),
        });
      } catch (e: any) {
        if (!cancelled) setServerError("Failed to load client.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchClient();
    return () => {
      cancelled = true;
    };
  }, [clientId, token]);

  // --------- Handlers ----------
  const setField =
    (name: keyof Form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const val = name === "loyaltyPoints" ? Number(e.target.value) : e.target.value;
      setForm((f) => ({ ...f, [name]: val }));
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

  const validate = (): boolean => {
    const e: Errors = {};
    if (!form.fullName.trim()) e.fullName = "Full name is required";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    if (form.phone && form.phone.replace(/\D/g, "").length < 6) e.phone = "Enter a valid phone";
    if (form.loyaltyPoints < 0) e.loyaltyPoints = "Points cannot be negative";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async (intent: "save" | "saveView") => {
    if (!validate()) return;
    try {
      setSubmitting(intent);
      setServerError(null);
      await api.put(`/clients/${clientId}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (intent === "saveView") {
        navigate(`/clients/${clientId}/profile`, { replace: true });
      } else {
        setSubmitting(false);
      }
    } catch (e: any) {
      setServerError(
        e?.response?.data?.message ||
          (e?.response?.status === 401 ? "Not authenticated. Please login." : "Failed to update client.")
      );
      setSubmitting(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    save("save");
  };

  // --------- UI helpers ----------
  const actionDisabled = submitting !== false || loading;

  // --------- Render ----------
  return (
    <Box
      p={{ xs: 2, md: 4 }}
      component={motion.div}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      sx={{
        background: (t) =>
          t.palette.mode === "light"
            ? `linear-gradient(135deg, ${t.palette.primary.light}0d, ${t.palette.background.default})`
            : undefined,
        minHeight: "100%",
      }}
    >
      <Container maxWidth="md">
        {/* Top row actions */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Button
            component={RouterLink}
            to="/clients"
            variant="text"
            startIcon={<ArrowBackIcon />}
            sx={{ fontWeight: 700 }}
          >
            Back to Clients
          </Button>

          <Button
            variant="outlined"
            startIcon={<ViewIcon />}
            onClick={() => navigate(`/clients/${clientId}/profile`)}
          >
            View Client Profile
          </Button>
        </Stack>

        {/* 🔷 Hero header */}
        <Box
          sx={(t) => ({
            mb: 3,
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 3 },
            borderRadius: 3,
            color: t.palette.getContrastText(t.palette.primary.main),
            background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 75%)`,
            boxShadow: `0 6px 18px ${t.palette.primary.main}33`,
            display: "flex",
            alignItems: "center",
            gap: 2,
            minHeight: 72,
          })}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              minWidth: 42,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.35)",
            }}
          >
            <PersonIcon sx={{ color: "white" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              fontWeight={900}
              sx={{ lineHeight: 1, color: "common.white" }}
              noWrap
            >
              Edit Client
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, color: "common.white" }} noWrap>
              Update contact details and loyalty points.
            </Typography>
          </Box>
        </Box>

        {serverError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {serverError}
          </Alert>
        )}

        {/* Form card */}
        <Card elevation={1} sx={{ borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
          <CardContent>
            {loading ? (
              <Box>
                <Skeleton height={38} sx={{ mb: 2 }} />
                <Skeleton height={38} sx={{ mb: 2 }} />
                <Skeleton height={38} sx={{ mb: 2 }} />
                <Skeleton height={38} sx={{ mb: 2 }} />
                <Skeleton height={38} sx={{ mb: 2 }} />
              </Box>
            ) : (
              <Box component="form" noValidate onSubmit={onSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      name="fullName"
                      label="Full Name"
                      value={form.fullName}
                      onChange={setField("fullName")}
                      error={!!errors.fullName}
                      helperText={errors.fullName}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email"
                      value={form.email}
                      onChange={setField("email")}
                      error={!!errors.email}
                      helperText={errors.email || "Optional"}
                      type="email"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="phone"
                      label="Phone"
                      value={form.phone}
                      onChange={setField("phone")}
                      error={!!errors.phone}
                      helperText={errors.phone || "Optional"}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        inputMode: "tel",
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="address"
                      label="Address"
                      value={form.address}
                      onChange={setField("address")}
                      helperText="Optional"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="loyaltyPoints"
                      label="Loyalty Points"
                      type="number"
                      value={form.loyaltyPoints}
                      onChange={setField("loyaltyPoints")}
                      error={!!errors.loyaltyPoints}
                      helperText={errors.loyaltyPoints || "Adjust points if needed"}
                      size="small"
                      inputProps={{ min: 0, step: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PointsIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                      justifyContent="flex-end"
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => navigate(`/clients/${clientId}/profile`)}
                        disabled={actionDisabled}
                        startIcon={<ViewIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        Cancel
                      </Button>

                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => save("save")}
                        disabled={actionDisabled}
                        startIcon={
                          submitting === "save" ? <CircularProgress size={18} /> : <SaveIcon />
                        }
                        sx={{ borderRadius: 2 }}
                      >
                        Save
                      </Button>

                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => save("saveView")}
                        disabled={actionDisabled}
                        startIcon={
                          submitting === "saveView" ? (
                            <CircularProgress size={18} />
                          ) : (
                            <SaveViewIcon />
                          )
                        }
                        sx={{ borderRadius: 2, fontWeight: 800 }}
                      >
                        Save & View Profile
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
